import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { PlayerVerticalSliceView } from '@aztec-realms/shared-types';
import { Modal, ResourceIcon } from '@aztec-realms/ui';

import { HomeBuildingArtwork } from './HomeBuildingArtwork';
import {
  personalGoalLabel,
  personalGoalPath,
  personalGoalSummary,
  useDailyProgress,
  useMissionDaily,
  usePlayerMissions,
  usePersonalEncounter,
  usePersonalRestoration,
  useResumePersonalEncounter,
  useRepairDaily,
  useSavePersonalGoal,
} from './data/personal-progress-api';
import { useNetworkStatus } from './network-status';
import { useJourneySafety } from './journey-safety';
import { personalGoalOptions } from './data/personal-goal-options';
import './personal-progress.css';

const materials = ['maize', 'stone', 'timber'] as const;
const dailyDayLabel = (count: number) => `${count} Daily ${count === 1 ? 'day' : 'days'}`;
const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

export function SavePersonalGoalButton({
  owner,
  buildingCode,
  buildingId,
  disabled,
  label,
}: {
  owner: string;
  buildingCode: string;
  buildingId?: string;
  disabled: boolean;
  label?: string;
}) {
  const save = useSavePersonalGoal(owner, disabled);
  return (
    <div className="personal-goal-save">
      <button
        type="button"
        disabled={disabled || save.isPending}
        onClick={() =>
          save.mutate({
            building_code: buildingCode === 'garden' ? 'garden_plaza' : buildingCode,
            ...(buildingId ? { building_id: buildingId } : {}),
          })
        }
      >
        {save.isPending
          ? 'Saving goal…'
          : save.isSuccess
            ? 'Goal saved'
            : (label ?? 'Make this my goal')}
      </button>
      {save.isSuccess && (
        <small role="status">Your goal travels with you. No materials were spent.</small>
      )}
      {save.error && <p role="alert">{save.error.message}</p>}
    </div>
  );
}

/** One compact entry; goals, optional calendar and chapter choices share a dismissible sheet. */
export function PersonalProgress({
  snapshot,
  surface,
}: {
  snapshot: PlayerVerticalSliceView;
  surface: 'home' | 'decode';
}) {
  const owner = snapshot.profile.userId ?? '';
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const locked = journey.safety.mode === 'journey-passive';
  const navigate = useNavigate();
  const restoration = usePersonalRestoration(owner, online && Boolean(owner));
  const daily = useDailyProgress(owner, online && Boolean(owner));
  const repair = useRepairDaily(owner, locked);
  const encounter = usePersonalEncounter(owner, locked);
  const resume = useResumePersonalEncounter(owner, locked);
  const [panel, setPanel] = useState<'goal' | 'daily' | 'chapter'>();
  const missions = usePlayerMissions(owner, panel === 'daily' && online && !locked);
  const missionDaily = useMissionDaily(owner, locked || !online);
  const currentMissions = missions.data?.missions.filter(
    (mission) =>
      mission.is_current &&
      mission.offer_kind !== 'legacy' &&
      mission.offer_kind !== 'onboarding' &&
      mission.availability !== 'history' &&
      ['daily', 'weekly'].includes(mission.scope),
  );
  const goal = restoration.data?.goal;
  const goalBuilding = snapshot.settlement.buildings.find(
    (building) => building.id === goal?.building_id,
  );
  const goalTitle = goal
    ? goal.status === 'completed'
      ? `Goal reached · ${personalGoalLabel(goal)}`
      : `${personalGoalLabel(goal)} · level ${goal.target_level}`
    : 'Choose what grows next';
  const goalOptions = personalGoalOptions(snapshot.settlement.buildings);
  const ownsGarden = snapshot.settlement.buildings.some((building) => building.type === 'garden');
  const chapter = restoration.data?.chapter;
  const days = daily.data;
  const busy =
    repair.isPending || encounter.isPending || resume.isPending || missionDaily.isPending;
  const unfinished = snapshot.decode.status !== 'solved';
  const unfinishedPersonal = unfinished && snapshot.decode.rewardMode === 'personal_materials';
  const blocked = locked || !online || busy;
  const go = (path: string) => {
    setPanel(undefined);
    navigate(path);
  };
  const play = () => {
    if (surface === 'home' || unfinished) go('/decode');
    else setPanel('chapter');
  };
  const dailyTitle = days
    ? `${dailyDayLabel(days.qualified_days)} · ${days.days_to_next_milestone} to a gift`
    : 'Daily milestones';
  if (!owner) return null;
  return (
    <>
      <section
        className={`personal-progress personal-progress--${surface}`}
        aria-label="Your personal progress"
      >
        {surface === 'home' ? (
          <button
            type="button"
            className="personal-progress__goal"
            disabled={locked}
            onClick={() => setPanel('goal')}
          >
            <HomeBuildingArtwork
              code={goal?.building_code ?? 'garden_plaza'}
              level={goal?.target_level ?? 1}
            />
            <span>
              <strong>{goalTitle}</strong>
              <small>
                {goal
                  ? personalGoalSummary(goal)
                  : 'A personal goal, with a path you can play at home.'}
              </small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        ) : (
          <button type="button" disabled={locked} onClick={() => setPanel('chapter')}>
            {unfinishedPersonal ? 'Your Garden chapter' : 'Garden chapter · no GPS'}
          </button>
        )}
        <button
          type="button"
          className="personal-progress__daily"
          disabled={locked}
          onClick={() => setPanel('daily')}
        >
          {dailyTitle}
        </button>
      </section>
      {panel &&
        !locked &&
        createPortal(
          <div className="personal-progress-overlay">
            <Modal
              open
              title={
                panel === 'daily'
                  ? 'Your Daily milestones'
                  : panel === 'chapter'
                    ? 'A garden of your own'
                    : 'Your chosen goal'
              }
              onClose={() => setPanel(undefined)}
            >
              <div className="personal-progress-sheet">
                {!online && (
                  <p role="status">Your saved progress stays here. Reconnect to continue.</p>
                )}
                {panel === 'daily' ? (
                  <>
                    <section className="daily-missions" aria-label="Your current goals">
                      <h3>Today and this week</h3>
                      <p>
                        Your Daily needs no GPS. These goals track play; gifts come every seven
                        played days.
                      </p>
                      {missions.isPending && online && (
                        <p role="status">Finding your current goals…</p>
                      )}
                      {missions.error && (
                        <p role="alert">
                          Your goals could not be refreshed.{' '}
                          <button disabled={blocked} onClick={() => void missions.refetch()}>
                            Retry goals
                          </button>
                        </p>
                      )}
                      {missions.data?.availability_note && (
                        <p role="status">{missions.data.availability_note}</p>
                      )}
                      {currentMissions?.length === 0 && (
                        <p>No current goals to show. Your earned Daily days stay yours.</p>
                      )}
                      <ul>
                        {currentMissions?.map((mission) => (
                          <li key={mission.id}>
                            <div className="daily-missions__heading">
                              <strong>{mission.title}</strong>
                              <span>
                                {mission.availability === 'completed'
                                  ? 'Complete'
                                  : `${mission.current} / ${mission.target}`}
                              </span>
                            </div>
                            <progress
                              max={mission.target}
                              value={mission.current}
                              aria-label={`${mission.title} progress`}
                            />
                            <small>
                              {mission.scope === 'daily'
                                ? 'Today'
                                : mission.scope === 'weekly'
                                  ? 'This week'
                                  : 'Your next step'}
                              {mission.offer_kind === 'daily_play' && ' · actual Daily play days'}
                            </small>
                            {mission.unavailable_reason && <p>{mission.unavailable_reason}</p>}
                            {mission.availability === 'available' &&
                              mission.action &&
                              mission.action !== 'daily' && (
                                <button
                                  disabled={blocked || missions.isError || missions.isFetching}
                                  onClick={() =>
                                    go(
                                      mission.action === 'settlement'
                                        ? '/home?panel=build'
                                        : '/decode',
                                    )
                                  }
                                >
                                  {mission.action === 'settlement'
                                    ? 'Visit your city'
                                    : 'Open Decode'}
                                </button>
                              )}
                          </li>
                        ))}
                      </ul>
                      {currentMissions?.some(
                        (mission) =>
                          mission.availability === 'available' && mission.action === 'daily',
                      ) && (
                        <>
                          {unfinished && (
                            <p>Your current glyph comes first. Your board and turns are kept.</p>
                          )}
                          <button
                            disabled={blocked || missions.isError || missions.isFetching}
                            onClick={() => {
                              if (unfinished) go('/decode');
                              else
                                missionDaily.mutate(undefined, {
                                  onSuccess: (result) => {
                                    if (result.kind === 'opened' || result.kind === 'resume')
                                      go('/decode');
                                    else void missions.refetch();
                                  },
                                });
                            }}
                          >
                            {missionDaily.isPending
                              ? 'Opening your Daily…'
                              : unfinished
                                ? 'Resume your glyph'
                                : 'Play your Daily'}
                          </button>
                        </>
                      )}
                      {missionDaily.data?.kind === 'complete' && (
                        <p role="status">
                          Today’s Daily is complete. Your next one opens{' '}
                          {new Date(missionDaily.data.availableAgainAt).toLocaleString([], {
                            weekday: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          in your local time.
                        </p>
                      )}
                      {missionDaily.error && <p role="alert">{missionDaily.error.message}</p>}
                    </section>
                    {daily.isPending && online && <p role="status">Opening your calendar…</p>}
                    {daily.error && (
                      <p role="alert">
                        Daily progress is unavailable.{' '}
                        <button onClick={() => void daily.refetch()}>Try again</button>
                      </p>
                    )}
                    {days && (
                      <>
                        <div className="daily-milestone">
                          <span className="realm-screen__eyebrow">
                            Every played day stays yours
                          </span>
                          <strong>{dailyDayLabel(days.qualified_days)} restored</strong>
                          <p>
                            {days.days_to_next_milestone} more Daily{' '}
                            {days.days_to_next_milestone === 1 ? 'day' : 'days'} to your next gift.
                          </p>
                          <progress
                            max={7}
                            value={7 - days.days_to_next_milestone}
                            aria-label="Played days toward the next seven-day milestone"
                          />
                          <div className="personal-materials" aria-label="Seven-day milestone gift">
                            {materials.map((resource) => (
                              <span key={resource}>
                                <ResourceIcon resource={resource} />
                                25 {resource}
                              </span>
                            ))}
                          </div>
                          <p>
                            Seven completed Daily days earn a gift and one ribbon repair, up to{' '}
                            {days.repair_save_cap} saved repairs. Days need not be consecutive.
                          </p>
                          {days.repair_saves === days.repair_save_cap && (
                            <small>
                              Your repairs are full. The next gift still includes materials; an
                              extra repair will not be stored.
                            </small>
                          )}
                          {days.cosmetic_milestone_earned && (
                            <strong className="daily-keepsake">✦ Thirty-day keepsake earned</strong>
                          )}
                        </div>
                        {!days.enabled && (
                          <p role="status">
                            New Daily milestones are paused. Your earned progress remains.
                          </p>
                        )}
                        {days.recent_milestones.slice(0, 1).map((milestone) => (
                          <section
                            key={milestone.cycle}
                            aria-label="Latest earned gift"
                            className="daily-earned-gift"
                          >
                            <strong>Your {milestone.qualified_days}-day gift is yours</strong>
                            <div className="personal-materials">
                              {materials.map((resource) =>
                                milestone.materials[resource] ? (
                                  <span key={resource}>
                                    <ResourceIcon resource={resource} />+
                                    {milestone.materials[resource]} {resource}
                                  </span>
                                ) : null,
                              )}
                            </div>
                            <small>
                              {milestone.save_granted
                                ? 'One ribbon repair also earned.'
                                : 'Your ribbon repairs were already full.'}
                            </small>
                          </section>
                        ))}
                        <details className="daily-ribbon">
                          <summary>Optional ribbon · {days.current_streak} connected days</summary>
                          <p>
                            {days.repair_saves} of {days.repair_save_cap} repairs saved. Longest
                            ribbon: {days.best_streak} days.
                          </p>
                          <p>
                            Repairs join a recent single gap between played days. They do not
                            complete a Daily or grant materials. All your played days and gifts stay
                            yours.
                          </p>
                          <ol>
                            {days.recent_days.map((day) => (
                              <li key={day.day} data-status={day.status}>
                                <time dateTime={day.day}>{dayLabel(day.day)}</time>
                                <span>
                                  {day.status === 'qualified'
                                    ? 'Played'
                                    : day.status === 'repaired'
                                      ? 'Repaired'
                                      : day.day === days.server_day
                                        ? 'Open today'
                                        : 'Rest day'}
                                </span>
                                {day.repairable && (
                                  <button
                                    type="button"
                                    disabled={blocked || !days.enabled || days.repair_saves < 1}
                                    onClick={() => repair.mutate(day.day)}
                                  >
                                    Repair {dayLabel(day.day)}
                                  </button>
                                )}
                              </li>
                            ))}
                          </ol>
                          {repair.isSuccess && (
                            <p role="status">
                              Ribbon repaired. Your played-day count is unchanged.
                            </p>
                          )}
                          {repair.error && <p role="alert">{repair.error.message}</p>}
                        </details>
                        <small>
                          One completed Daily counts per UTC day. Next day opens{' '}
                          {new Date(days.next_day_at).toLocaleString([], {
                            weekday: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          in your local time.
                        </small>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {restoration.isPending && online && (
                      <p role="status">Finding your next step…</p>
                    )}
                    {restoration.error && (
                      <p role="alert">
                        Your goal could not be loaded.{' '}
                        <button onClick={() => void restoration.refetch()}>Try again</button>
                      </p>
                    )}
                    {restoration.data?.enabled === false && (
                      <p role="status">
                        New personal restorations are paused. Your goal and chapter remain saved.
                      </p>
                    )}
                    {panel === 'goal' && (
                      <>
                        <HomeBuildingArtwork
                          code={goal?.building_code ?? 'garden_plaza'}
                          level={goal?.target_level ?? 1}
                          variant="catalog"
                        />
                        <h3>{goalTitle}</h3>
                        <p>
                          {goal
                            ? personalGoalSummary(goal)
                            : 'Pick a goal to carry between your city and your puzzles. Choosing spends nothing.'}
                        </p>
                        {goal?.status === 'completed' && goalBuilding && (
                          <p>
                            Your {goalBuilding.label} already meets the level {goal.target_level}{' '}
                            goal. Current building: level {goalBuilding.level}
                            {goalBuilding.state === 'constructing' ? ' · upgrade underway' : ''}.
                          </p>
                        )}
                        {goal && (
                          <>
                            <dl className="personal-goal-costs">
                              {Object.entries(goal.costs).map(([code, value]) => (
                                <div key={code}>
                                  <dt>{code}</dt>
                                  <dd>
                                    {value} total · {goal.deficits[code] ?? 0} still needed
                                  </dd>
                                </div>
                              ))}
                            </dl>
                            <button disabled={blocked} onClick={() => go(personalGoalPath(goal))}>
                              {goal.status === 'ready'
                                ? 'Review construction'
                                : 'View goal in your city'}
                            </button>
                            <button disabled={blocked} onClick={play}>
                              {unfinished
                                ? 'Resume your glyph'
                                : goal.status === 'completed'
                                  ? 'Continue playing'
                                  : 'Play toward your goal'}
                            </button>
                          </>
                        )}
                        {!goal && !ownsGarden && restoration.data && (
                          <SavePersonalGoalButton
                            owner={owner}
                            buildingCode="garden_plaza"
                            label="Make a new Garden Plaza my goal · level 1"
                            disabled={blocked || restoration.data?.enabled === false}
                          />
                        )}
                        {restoration.data && goalOptions.length > 0 && (
                          <details
                            className="personal-goal-options"
                            open={!goal || goal.status === 'completed'}
                          >
                            <summary>Choose an existing building to develop</summary>
                            <p>
                              Choose the next level, or finish work already underway. No materials
                              are spent.
                            </p>
                            {goalOptions.map(({ building, target, label }) => (
                              <div key={building.id}>
                                <small>
                                  {snapshot.settlement.districts.find(
                                    (district) => district.code === building.districtCode,
                                  )?.displayName ?? building.districtCode}{' '}
                                  · plot {building.gridX}, {building.gridY}
                                </small>
                                <SavePersonalGoalButton
                                  key={`${building.id}:${target}`}
                                  owner={owner}
                                  buildingCode={building.type}
                                  buildingId={building.id}
                                  label={label}
                                  disabled={
                                    blocked ||
                                    restoration.data?.enabled === false ||
                                    (goal?.building_id === building.id &&
                                      goal.target_level === target &&
                                      goal.status !== 'completed')
                                  }
                                />
                              </div>
                            ))}
                          </details>
                        )}
                        <button disabled={locked} onClick={() => go('/home?panel=build')}>
                          Browse new buildings
                        </button>
                        <button disabled={blocked} onClick={() => setPanel('chapter')}>
                          Visit your Garden chapter
                        </button>
                      </>
                    )}
                    {panel === 'chapter' && chapter && (
                      <>
                        <HomeBuildingArtwork code="garden_plaza" variant="catalog" />
                        <p>Restore garden glyphs at home. No GPS or Tribe needed.</p>
                        <p>
                          {chapter.daily_completed} of {chapter.daily_limit} material restorations
                          completed today.
                        </p>
                        <p>Earn a small material gift with each garden restoration.</p>
                        {unfinished && (
                          <p role="status">
                            {unfinishedPersonal
                              ? 'Your current garden glyph is waiting.'
                              : 'Finish your current glyph first. Your board and turns are kept.'}
                          </p>
                        )}
                        {chapter.active_session_id && !unfinishedPersonal && (
                          <button
                            disabled={blocked || restoration.data?.enabled === false}
                            onClick={() =>
                              resume.mutate(chapter.active_session_id!, {
                                onSuccess: () => go('/decode'),
                              })
                            }
                          >
                            Resume saved Garden glyph
                          </button>
                        )}
                        {unfinished && (
                          <button disabled={blocked} onClick={() => go('/decode')}>
                            Resume your glyph
                          </button>
                        )}
                        <div className="personal-chapter-choices">
                          {chapter.encounters.map((option) => (
                            <section key={option.code}>
                              <strong>{option.title}</strong>
                              <small>{option.profile_code} restoration</small>
                              <div className="personal-materials">
                                {materials.map((resource) =>
                                  option.rewards[resource] ? (
                                    <span key={resource}>
                                      <ResourceIcon resource={resource} />
                                      {option.rewards[resource]} {resource}
                                    </span>
                                  ) : null,
                                )}
                              </div>
                              {!option.available && <p>{option.locked_reason}</p>}
                              <button
                                type="button"
                                disabled={
                                  blocked ||
                                  restoration.data?.enabled === false ||
                                  !option.available ||
                                  unfinished
                                }
                                onClick={() =>
                                  encounter.mutate(option.code, { onSuccess: () => go('/decode') })
                                }
                              >
                                Open {option.title}
                              </button>
                            </section>
                          ))}
                        </div>
                        {encounter.error && <p role="alert">{encounter.error.message}</p>}
                        {resume.error && <p role="alert">{resume.error.message}</p>}
                        {!chapter.continuation_unlocked && (
                          <p>Complete a Garden Plaza to open its resonance challenge.</p>
                        )}
                        <small>
                          The next material day opens{' '}
                          {new Date(chapter.next_reset_at).toLocaleString([], {
                            weekday: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          .
                        </small>
                        {goal && (
                          <button disabled={blocked} onClick={() => go(personalGoalPath(goal))}>
                            Return to {personalGoalLabel(goal)}
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </Modal>
          </div>,
          document.querySelector('.ar-game-shell') ?? document.body,
        )}
    </>
  );
}
